import { getMemoizedValues } from 'app/selectors';
import { setMemoizedValuesSlice } from 'features/memoizedValues/memoizedValuesSlice';

import * as R from 'ramda';
import type { SetStateAction } from 'react';
import { useEffect, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useMatch } from 'react-router-dom';

import { usePreviousValue } from './usePrevious';

type TFilterSetter = ({
    savedValuesArray,
    savedValuesObj,
    filterSetter,
    contextTableId,
}: {
    savedValuesObj: any | null;
    savedValuesArray: any[] | null;
    filterSetter?: ({ key, newState }: TFilterSaver) => void;
    contextTableId?: string;
}) => void;

type TFilterSaver = {
    key: string | undefined;
    newState: any;
    rootKey?: string;
};
type TEmptyFilterSetter = (localSaver: (params: TFilterSaver) => void) => void;

type TFilterSetterProps = {
    filterSetter?: TFilterSetter | null;
    withEmptyFilterSetter?: TEmptyFilterSetter | null;
    customSaveKey?: string;
    withFakeSetter?: boolean;
    matchPathPattern?: string;
    canSendRequestTimeout?: number;
    unableToProcess?: boolean;
};

type TWithDefaultTransformerFilterSetter = (args: { fieldName: string; checkedValues: string[] }) => void;

type TWithDefaultTransformer = {
    (
        setter: TWithDefaultTransformerFilterSetter,
        contextTableId: string,
        filterCleaner: () => void,
        forceClean?: boolean,
    ): TFilterSetter;
    (
        setter: TWithDefaultTransformerFilterSetter,
        contextTableId?: never,
        filterCleaner?: never,
        forceClean?: never,
    ): TFilterSetter;
};
type TCustomOrPathKeyType =
    | { customNameKey: string; pathNameKey?: never }
    | {
          customNameKey?: never;
          pathNameKey: string;
      };

export const getParamsValuesKey = (pathname: string | undefined) =>
    pathname ? pathname.split('/').filter(Boolean).join('_') : null;

export const usePageFilterSaver = (props: TFilterSetterProps = {}) => {
    const dispatch = useDispatch();

    const {
        withEmptyFilterSetter,
        filterSetter,
        withFakeSetter,
        customSaveKey,
        matchPathPattern = '',
        canSendRequestTimeout = 100,
        unableToProcess,
    } = props;

    const { pathname } = useLocation();
    const match = useMatch({
        path: matchPathPattern,
        end: true,
    });

    const canProcessWithMatch = matchPathPattern ? !!match : true;

    const [canSendRequest, setCanSendRequest] = useState(false);

    const memoizedValues = useSelector(getMemoizedValues);
    const filterKey = useMemo(() => customSaveKey ?? `${getParamsValuesKey(pathname)}`, [customSaveKey, pathname]);

    const handleSaveFilterState = useCallback(
        ({ key, newState, rootKey }: TFilterSaver) => {
            if (key && pathname) {
                const newValue = rootKey
                    ? {
                          [rootKey]: {
                              ...memoizedValues?.[filterKey]?.[rootKey],
                              [key]: newState,
                          },
                      }
                    : {
                          [key]: newState,
                      };

                dispatch(
                    setMemoizedValuesSlice({
                        key: filterKey,
                        value: newValue,
                    }),
                );
            }
        },
        [dispatch, filterKey, memoizedValues, pathname],
    );

    const savedFilterState = useMemo(() => memoizedValues[filterKey], [filterKey, memoizedValues]);

    const withSavedHandler = useCallback(() => {
        const savedFilterStateArray: [string, any][] | null = savedFilterState
            ? Object.entries(savedFilterState)
            : null;

        if (savedFilterState && (filterSetter || withFakeSetter)) {
            filterSetter?.({
                savedValuesObj: savedFilterState,
                savedValuesArray: savedFilterStateArray,
                filterSetter: handleSaveFilterState,
            });

            setTimeout(() => {
                setCanSendRequest(true);
            }, 0);
        }
    }, [filterSetter, handleSaveFilterState, savedFilterState, withFakeSetter]);

    const prevSavedFilters = usePreviousValue(savedFilterState);

    useEffect(() => {
        if (canProcessWithMatch && !unableToProcess) {
            if (savedFilterState || (memoizedValues && savedFilterState === undefined)) {
                if (R.equals(prevSavedFilters, savedFilterState)) {
                    withSavedHandler();
                }

                if (!savedFilterState && (withEmptyFilterSetter || withFakeSetter)) {
                    withEmptyFilterSetter?.(handleSaveFilterState);

                    setTimeout(() => {
                        setCanSendRequest(true);
                    }, 0);
                }
            }
        }
    }, [
        canProcessWithMatch,
        handleSaveFilterState,
        memoizedValues,
        prevSavedFilters,
        savedFilterState,
        unableToProcess,
        withEmptyFilterSetter,
        withFakeSetter,
        withSavedHandler,
    ]);

    useEffect(() => {
        if (canSendRequest) {
            setTimeout(() => {
                setCanSendRequest(false);
            }, canSendRequestTimeout);
        }
    }, [canSendRequest, canSendRequestTimeout]);

    useEffect(() => {
        withSavedHandler();
    }, [withSavedHandler]);

    const handleGetMemoizedValues = useCallback(
        (
            params:
                | TCustomOrPathKeyType
                | {
                      customNameKey?: never;
                      pathNameKey?: never;
                  },
        ) => {
            const { customNameKey, pathNameKey } = params ?? {};

            if (customNameKey) {
                return memoizedValues[customNameKey];
            }

            if (pathNameKey) {
                const key = getParamsValuesKey(pathNameKey);

                return key ? memoizedValues[key] : null;
            }

            return memoizedValues;
        },
        [memoizedValues],
    );

    const handleCleanEntityMemoizedValues = useCallback(
        ({ customNameKey, pathNameKey }: TCustomOrPathKeyType) => {
            if (customNameKey) {
                dispatch(
                    setMemoizedValuesSlice({
                        key: customNameKey,
                        value: undefined,
                    }),
                );
            }

            if (pathNameKey) {
                const key = getParamsValuesKey(pathNameKey);

                if (key) {
                    dispatch(
                        setMemoizedValuesSlice({
                            key,
                            value: undefined,
                        }),
                    );
                }
            }
        },
        [dispatch],
    );

    return useMemo(
        () => ({
            canSendRequest,
            savedFilterState,
            handleSaveFilterState,
            handleGetMemoizedValues,
            handleCleanEntityMemoizedValues,
        }),
        [
            canSendRequest,
            handleCleanEntityMemoizedValues,
            handleGetMemoizedValues,
            handleSaveFilterState,
            savedFilterState,
        ],
    );
};

export const withDefaultTransformer: TWithDefaultTransformer =
    (setter, contextTableId, filterCleaner, forceClean?: boolean) =>
    ({ savedValuesObj, filterSetter }) => {
        if (contextTableId && savedValuesObj?.[contextTableId]) {
            filterCleaner?.();

            if (!forceClean) {
                Object.entries(savedValuesObj[contextTableId]).forEach((savedValue) => {
                    const [fieldName, fieldValue] = savedValue;

                    if (Array.isArray(fieldValue)) {
                        setter({
                            fieldName,
                            checkedValues: fieldValue?.map(({ value }) => value),
                        });
                    }
                });
            } else {
                filterSetter?.({
                    key: contextTableId,
                    newState: undefined,
                });
            }

            return;
        }

        if (contextTableId && !savedValuesObj?.[contextTableId]) {
            filterCleaner?.();

            return;
        }

        Object.entries(savedValuesObj).forEach((savedValue) => {
            const [fieldName, fieldValue] = savedValue;

            if (Array.isArray(fieldValue)) {
                setter({
                    fieldName,
                    checkedValues: fieldValue?.map(({ value }) => value),
                });
            }
        });
    };

export const getDefaultFilterCleaner =
    ({
        changeFilterHandler,
        setFiltersWhere,
    }: {
        changeFilterHandler: TWithDefaultTransformerFilterSetter;
        setFiltersWhere?: SetStateAction<any>;
    }) =>
    (fieldName?: string) => {
        if (fieldName) {
            changeFilterHandler({
                fieldName,
                checkedValues: [],
            });
        } else {
            setFiltersWhere(undefined);
        }
    };
